import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';

import { ImportStatusError } from './import-status-error';
import { ImportStatusInProgress } from './import-status-in-progress';
import { ImportStatusQueued } from './import-status-queued';
import { ImportStatusSuccess } from './import-status-success';
import { ImportStatusUnknown } from './import-status-unknown';
import { messages } from './messages';
import { StoreErrorInfoMessage } from './store-error-info-message';
import { StoreUpdatingInfoMessage } from './store-updating-info-message';
import style from './style.module.less';
import DataDrivenLogo from '../../../../../../components/data-driven-logo';
import SelectMarketplaceModal from '../../../../../../pages/settings/stores/select-marketplace-modal';
import { EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { useMarketplaceContext } from '../../context/marketplace-context';

const IDLE = 'Idle';
const QUEUED = 'Queued';
const UPDATING = 'Updating';
const ERROR = 'Error';
// These are the available status, they are all upcase to match the API
const STORE_REFRESH_STATUS = {
  [IDLE]: ImportStatusSuccess,
  [QUEUED]: ImportStatusQueued,
  [UPDATING]: ImportStatusInProgress,
  [ERROR]: ImportStatusError,
};

const renderStoreStatus = (store) => {
  const { lastRefreshAttemptedDate, refreshStatus } = store;
  const StatusComponent = STORE_REFRESH_STATUS[refreshStatus];

  if ((!lastRefreshAttemptedDate && refreshStatus === IDLE) || !StatusComponent) {
    return <ImportStatusUnknown />;
  }

  return <StatusComponent />;
};

const ConnectedStoreItem = ({ store }) => {
  const { marketplaceId, refreshStatus } = store;
  const isUpdating = refreshStatus === UPDATING;
  const isError = refreshStatus === ERROR;

  return (
    <div className={style['connected-store-item']}>
      <div className={style['store-header']}>
        <div className={style['store-info']}>
          <DataDrivenLogo className={style['marketplace-icon']} logoType='icon' marketplaceId={marketplaceId} />
          <Text textType={TextType.H3} value={store.name} />
          <span className={style['connection-status']}>
            <span className={style['connection-status-dot']}>&middot;</span>
            <FormattedMessage {...messages.connectedStatus} />
          </span>
        </div>
        <div>{renderStoreStatus(store)}</div>
      </div>
      {isUpdating ? <StoreUpdatingInfoMessage /> : null}
      {isError ? <StoreErrorInfoMessage /> : null}
    </div>
  );
};

// Component for the "Connect another store" button
const ConnectAnotherStoreButton = ({ onClick }) => {
  return (
    <Button className={style['connect-another-store']} onClick={onClick} theme='link'>
      <FormattedMessage {...messages.connectAnotherStore} />
    </Button>
  );
};

export const ImportMarketplaceOrdersOption = ({ activeStores }) => {
  const [showMarketplaceModal, setShowMarketplaceModal] = useState(false);
  const { onMarketplaceSelected, startMarketplaceConnection, cancelMarketplaceConnection } = useMarketplaceContext();

  const handleConnectAnotherStore = () => {
    startMarketplaceConnection(EventSourceStrings.FTU_WIZARD_ADD_ANOTHER_STORE);
    setShowMarketplaceModal(true);
  };

  const handleMarketplaceSelected = (marketplace) => {
    onMarketplaceSelected(marketplace);
    setShowMarketplaceModal(false);
  };

  const handleCloseModal = () => {
    cancelMarketplaceConnection(EventSourceStrings.FTU_WIZARD_ADD_ANOTHER_STORE);
    setShowMarketplaceModal(false);
  };

  return (
    <div className={style['import-marketplace-orders']}>
      {activeStores.map((store) => (
        <ConnectedStoreItem key={store.storeGuid} store={store} />
      ))}
      <ConnectAnotherStoreButton onClick={handleConnectAnotherStore} />
      {showMarketplaceModal ? (
        <SelectMarketplaceModal onClose={handleCloseModal} onMarketplaceSelected={handleMarketplaceSelected} />
      ) : null}
    </div>
  );
};
